import { useEffect, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { Loader } from '@/components/ui/Loader';
import { CallerName } from '@/components/crm/CallerName';
import { formatDuration } from '@/utils/helpers';
import type { ClassifiedCall } from '@/lib/reports/callClassification';
import type { CallRecord, Extension } from '@/types';
import { CallDetailPanel } from './CallDetailPanel';
import { MissedCallsList } from './MissedCallsList';
import { filterClassifiedCalls, STAT_CALL_TITLES, type StatCallKind } from './statCalls';

interface StatCallsModalProps {
  kind: StatCallKind | null;
  /** The active range's calls; null while they load. */
  calls: ClassifiedCall[] | null;
  /** The CDR rows behind them, for the detail view. */
  records: CallRecord[] | null;
  failed: boolean;
  rangeLabel: string;
  /** The range covers more than one day. */
  multiDay: boolean;
  extensions: Extension[];
  onClose: () => void;
}

const clock = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const dayClock = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

function result(call: ClassifiedCall): { text: string; variant: 'success' | 'danger' | 'warning' | 'default' } {
  if (call.direction === 'outbound') {
    return call.talkSeconds > 0 ? { text: 'Answered', variant: 'success' } : { text: 'Not answered', variant: 'default' };
  }
  switch (call.outcome) {
    case 'answered':
      return { text: 'Answered', variant: 'success' };
    case 'voicemail':
      return { text: 'Voicemail', variant: 'warning' };
    case 'missed':
      return { text: 'Missed', variant: 'danger' };
    default:
      return { text: 'Hung up in IVR', variant: 'default' };
  }
}

/** Who the call ended up with: the extension that answered, or the last place it reached. */
function destination(call: ClassifiedCall): string {
  if (call.direction === 'outbound' || call.outcome === 'answered') return call.record.call_to;
  return call.route[call.route.length - 1]?.label ?? call.record.call_to;
}

export function StatCallsModal({
  kind,
  calls,
  records,
  failed,
  rangeLabel,
  multiDay,
  extensions,
  onClose,
}: StatCallsModalProps) {
  const isOpen = kind !== null;
  const [selected, setSelected] = useState<ClassifiedCall | null>(null);

  useEffect(() => setSelected(null), [kind, calls]);

  const shown = useMemo(
    () => (kind && calls ? filterClassifiedCalls(calls, kind).slice().reverse() : []),
    [calls, kind]
  );

  const title = kind ? `${STAT_CALL_TITLES[kind]} · ${rangeLabel}` : '';
  const time = multiDay ? dayClock : clock;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="xl">
      {calls === null || records === null ? (
        <Loader text="Loading calls..." />
      ) : failed ? (
        <p className="py-8 text-center text-sm text-red-600 dark:text-red-400">
          Could not load the call details from the phone system. Use Refresh to try again.
        </p>
      ) : selected ? (
        <CallDetailPanel
          record={selected.record}
          records={records}
          extensions={extensions}
          backLabel={`Back to ${STAT_CALL_TITLES[kind ?? 'total'].toLowerCase()}`}
          onBack={() => setSelected(null)}
        />
      ) : kind === 'missed' ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Callers who never spoke to anyone, newest first. Click a call for details.
          </p>
          <MissedCallsList calls={shown} showDate={multiDay} onSelect={setSelected} />
        </div>
      ) : shown.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
          No calls found for this period.
        </p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {shown.length} {shown.length === 1 ? 'call' : 'calls'}. Click a call for details.
          </p>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm text-gray-900 dark:text-gray-100">
              <thead className="bg-gray-50 dark:bg-gray-700 text-left text-gray-700 dark:text-gray-200">
                <tr>
                  <th className="px-3 py-2 font-semibold">Time</th>
                  <th className="px-3 py-2 font-semibold">From</th>
                  <th className="px-3 py-2 font-semibold">To</th>
                  <th className="px-3 py-2 font-semibold">Result</th>
                  <th className="px-3 py-2 text-right font-semibold">Talk time</th>
                  <th className="w-8 px-2 py-2">
                    <span className="sr-only">Details</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {shown.map((call) => {
                  const { text, variant } = result(call);
                  return (
                    <tr
                      key={call.uid}
                      onClick={(event) => {
                        // A contact name is its own button
                        if (!(event.target as HTMLElement).closest('button')) setSelected(call);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setSelected(call);
                        }
                      }}
                      tabIndex={0}
                      title="Show call details"
                      className="cursor-pointer hover:bg-gray-50 focus:bg-gray-50 focus:outline-none dark:hover:bg-gray-700/60 dark:focus:bg-gray-700/60"
                    >
                      <td className="whitespace-nowrap px-3 py-2 text-gray-600 dark:text-gray-300">
                        {call.atMs ? time.format(new Date(call.atMs)) : call.time}
                      </td>
                      <td className="px-3 py-2">
                        <CallerName value={call.direction === 'outbound' ? call.record.call_from : call.caller} compact />
                      </td>
                      <td className="px-3 py-2">
                        <CallerName value={destination(call)} compact />
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant={variant}>{text}</Badge>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right">
                        {formatDuration(call.talkSeconds)}
                      </td>
                      <td className="px-2 py-2 text-gray-400">
                        <ChevronRight size={16} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}
